import React, { useCallback, useState } from "react";
import { BlockStack, Box, Button, Divider, Icon, InlineStack, List, Modal, Text } from "@shopify/polaris";
import { AddNoteMajor, ArrowDownMinor, ViewMinor } from "@shopify/polaris-icons";
import { useAuthenticatedFetch } from "@/Api/Axios";

export default function ShowResumeScore({ row, jobData, childRef }) {
  const [summaryActive, setSummaryActive] = useState(false);
  const [showResumeActive, setShowResumeActive] = useState(false);
  const fetch = useAuthenticatedFetch();

  const toggleSummary = useCallback(() => setSummaryActive((popoverActive) => !popoverActive), []);
  const toggleResume = useCallback(() => setShowResumeActive((popoverActive) => !popoverActive), []);

  const getResumeScore = async (candidate) => {
    const data = {
      jobData,
      candidateResume: candidate?.resume?.assetUrl,
      candidateId: candidate?._id,
      candidate,
    };
    await fetch.post(`/candidateResumeScore`, data);
    childRef?.current?.fetchData();
  };

  const handleDownloadFile = async (url) => {
    let resumeurl = `${process.env.STORAGE_BASE_URL}/${url}`;
    const res = await fetch.post("/generate-presigned-url", {
      imagePath: resumeurl,
    });

    const signedUrl = res.data;

    // Step 2: Create a hidden link element for the direct download
    const link = document.createElement("a");
    link.href = signedUrl; // Use the pre-signed URL
    link.download = url || "downloaded-image.jpg"; // Default filename if none provided
    link.style.display = "none";

    // Step 3: Append the link, trigger the download, and remove the link
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <BlockStack gap={300}>
      {row?.resumeScore?.summary && (
        <Button monochrome onClick={() => toggleSummary(row)}>
          <InlineStack gap={100}>
            <Icon source={AddNoteMajor}></Icon>
            <Text fontWeight="medium"> Resume Summary</Text>
          </InlineStack>
        </Button>
      )}

      <Button monochrome onClick={() => handleDownloadFile(row?.resume?.assetUrl)}>
        <InlineStack gap={100}>
          <Icon source={ArrowDownMinor}></Icon>
          <Text fontWeight="medium">Download Resume</Text>
        </InlineStack>
      </Button>

      {!row?.resumeScore?.score && (
        <Button monochrome onClick={() => getResumeScore(row)}>
          <InlineStack gap={100}>
            <Icon source={AddNoteMajor}></Icon>
            <Text fontWeight="medium">Get Resume Score</Text>
          </InlineStack>
        </Button>
      )}
      {row?.resume?.assetUrl && (
        <Button monochrome onClick={() => toggleResume(row)}>
          <InlineStack gap={100}>
            <Icon source={ViewMinor} />
            <Text fontWeight="medium">View Resume</Text>
          </InlineStack>
        </Button>
      )}
      <Modal
        open={summaryActive}
        onClose={toggleSummary}
        size="large"
        title={
          row?.fullName
            ? `${row?.fullName?.charAt(0)?.toUpperCase() + row?.fullName?.slice(1)}'s summary`
            : "summary"
        }
      >
        <Modal.Section>
          <div style={{ fontSize: "15px", padding: "10px" }}>
            <BlockStack gap={300}>
              <Text>
                <b>Resume Summary : </b>
                {row?.resumeScore?.summary}
              </Text>
              <Divider />

              <InlineStack gap={600}>
                <Box minWidth="30%" maxWidth="48%">
                  <style
                    dangerouslySetInnerHTML={{
                      __html: `.Polaris-List--typeNumber{padding-left:15px;} .Polaris-List__Item{font-size:inherit} .Polaris-Text--bodySm{max-width: 24rem}`,
                    }}
                  />
                  <BlockStack gap={300}>
                    <Text fontWeight="bold">Matched Skills</Text>
                    <Divider />
                    <List type="number" padding="0">
                      {row?.resumeScore?.mentions_points?.map((e, index) => (
                        <List.Item key={index}>{e}</List.Item>
                      ))}
                    </List>
                  </BlockStack>
                </Box>

                <Box maxWidth="48%">
                  <BlockStack gap={300}>
                    <Text fontWeight="bold">Unmatched Skills</Text>
                    <Divider />
                    <List type="number" padding="0">
                      {row?.resumeScore?.not_mentions_points?.map((e, index) => (
                        <List.Item key={index}>{e}</List.Item>
                      ))}
                    </List>
                  </BlockStack>
                </Box>
              </InlineStack>
            </BlockStack>
          </div>
        </Modal.Section>
      </Modal>
      <Modal
        open={showResumeActive}
        onClose={toggleResume}
        size="large"
        title={`${row?.fullName ? `${row?.fullName}'s` : ""} resume`}
      >
        <div style={{ height: "calc(100vh - 250px)" }}>
          {row?.resume?.assetUrl?.includes(".pdf") && (
            <object
              data={`${process.env.STORAGE_BASE_URL}/${row?.resume?.assetUrl}`}
              type="application/pdf"
              width="100%"
              height="100%"
            >
              <p>PDF cannot be displayed.</p>
            </object>
          )}
          {(row?.resume?.assetUrl?.includes(".docx") || row?.resume?.assetUrl?.includes(".doc")) && (
            <iframe
              src={`https://docs.google.com/gview?url=${process.env.STORAGE_BASE_URL}/${row?.resume?.assetUrl}&embedded=true`}
              width="100%"
              height="100%"
            />
          )}
        </div>
      </Modal>
    </BlockStack>
  );
}
